'use client';

import { useRef, useState, type PointerEvent } from 'react';

/**
 * Open state for a Popover standing in for a Tooltip, so an explanation reads
 * the same to a mouse and stays reachable without one.
 *
 * Radix tooltips never open on tap, which is why these explanations are
 * disclosures: a Popover opens on tap, click and keyboard on its own. What it
 * does not give is hover, so the returned handlers restore it, guarded to
 * `pointerType === 'mouse'` so a tap does not open and close in the same
 * gesture. Spread `hoverProps` on the content as well as the trigger, or
 * crossing the gap between them dismisses it, and spread `contentProps` on the
 * content: its Escape and focus handlers only keep focus in place together.
 */
export const useHoverablePopover = () => {
  const [open, setOpen] = useState(false);
  const openedByHover = useRef(false);
  const closedByHover = useRef(false);
  // Opened by a tap, click or key. A mouse passing over it afterwards must not
  // take it over: leaving would close it and skip the focus hand-back, which
  // drops a keyboard user's focus to the body.
  const openedByActivation = useRef(false);

  const hoverProps = {
    onPointerEnter: (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || openedByActivation.current) return;
      openedByHover.current = true;
      setOpen(true);
    },
    onPointerLeave: (event: PointerEvent) => {
      if (event.pointerType !== 'mouse' || openedByActivation.current) return;
      closedByHover.current = openedByHover.current;
      openedByHover.current = false;
      setOpen(false);
    },
  };

  // A mouse click lands on a trigger the same mouse just hovered open, and the
  // disclosure reads that as a request to toggle shut. Closing there would make
  // the explanation flash and vanish under the very gesture meant to pin it, so
  // a close is refused while the pointer is still on it and left to the leave
  // handler. Taps and keys never set the flag, so they toggle normally.
  const onOpenChange = (next: boolean) => {
    if (!next && openedByHover.current) return;
    if (next) openedByHover.current = false;
    openedByActivation.current = next;
    setOpen(next);
  };

  // Pointing at the trigger must not pull focus off whatever the user was on;
  // activating it still moves focus into the content as usual.
  const onOpenAutoFocus = (event: Event) => {
    if (openedByHover.current) event.preventDefault();
  };

  // Closing hands focus back to the trigger, and a focus set from script draws
  // the focus ring. After a hover that leaves a ring on something the user only
  // pointed at, so the hand-back is skipped there; a close by tap or key keeps it.
  const onCloseAutoFocus = (event: Event) => {
    if (closedByHover.current) event.preventDefault();
    closedByHover.current = false;
  };

  // Escape reaches `onOpenChange` as the same close request a click on the
  // trigger makes, which the hover guard above refuses. Content shown on hover
  // must still be dismissible without moving the pointer, so Escape lifts the
  // guard first. With `onCloseAutoFocus` alongside, focus stays where it was, as
  // it does after any hover.
  const onEscapeKeyDown = () => {
    if (!openedByHover.current) return;
    openedByHover.current = false;
    closedByHover.current = true;
  };

  const contentProps = { onOpenAutoFocus, onCloseAutoFocus, onEscapeKeyDown };

  return { open, onOpenChange, hoverProps, contentProps };
};
